import type { MemberSkill, Skill, SkillLevel } from '@forecastify/shared';
import {
  Button,
  DropdownMenu,
  Icon,
  SKELETON_WIDTHS,
  Skeleton,
  skeletonWidth,
  type MenuItem,
} from '@forecastify/ui';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Fiókom → Skilljeim. C54.                                                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The signed-in person's own skills, strongest first, each with the level they hold in it.
 *
 * ⚠️⚠️ **THE LEVEL IS CHANGED IN PLACE FROM THE ROW, NOT IN A DIALOG.** The mockup's row carries a
 * 132px listbox button, and every item in it carries the level's own description under its name — which
 * is the only place in the product where *"what does Tapasztalt actually mean"* is answerable at
 * the moment somebody is deciding it. A dialog per row would bury it.
 *
 * ⚠️ **Remove is a ghost button that appears on hover**, as the mockup draws it — but it is a real
 * `<button>` with an `aria-label`, so it is reachable by keyboard and by a screen reader whatever
 * the opacity says.
 *
 * ⚠️⚠️ **ONE SENTENCE OF THE MOCKUP'S EMPTY STATE IS NOT SHIPPED, AND IT IS FLAGGED RATHER THAN
 * QUIETLY DROPPED.** Its words are *"vegyél fel egyet — a vezetőd és a projektvezetők ez alapján
 * keresnek rád"* — your manager and the project leads search for you on this. **Nothing in the
 * product does that.** Phase 5 builds a vocabulary and no matcher (`0149` and `skill.service.ts`
 * both say so), so the sentence promises a feature that does not exist and would be read as one
 * by the first person who saw it. The rest of the empty state is the mockup's.
 */

export interface AccountSkillsSectionProps {
  readonly skills: readonly MemberSkill[];
  readonly levels: readonly SkillLevel[];
  /** ⚠️ The whole vocabulary, so *Skill hozzáadása* knows what is left to offer. */
  readonly vocabulary: readonly Skill[];
  readonly serverError: string | null;
  /** Until the first read lands: skeleton rows instead of the empty sentence, and no count. */
  readonly loading?: boolean;
  /**
   * C54.1 · ⚠️ **The person page reuses this component for somebody ELSE's skills.** Reading
   * them needs `people.view` and changing them needs `people.manage`, so a colleague without the
   * second sees the table and none of the controls — what somebody is good at is not secret.
   */
  readonly readOnly?: boolean;
  /**
   * ⚠️ **Whose skills these are, for the add dialog's sentence.** Absent means the signed-in
   * person, which is the account screen and the wording it has always had.
   */
  readonly subjectName?: string;
  readonly onSetLevel: (skillId: string, levelId: string) => void;
  readonly onAdd: (skillId: string, levelId: string) => void;
  readonly onRemove: (skillId: string) => void;
}

export function AccountSkillsSection(props: AccountSkillsSectionProps): React.JSX.Element {
  const t = useT();
  const { skills, levels, vocabulary } = props;
  /* C54.1 · absent means the account screen, which has always been editable. */
  const canEdit = props.readOnly !== true;

  /**
   * ⚠️ **What is left to add**, which is the mockup's `skPickVals`: a skill somebody already holds
   * is not offered again, because holding it twice is not a thing the schema allows.
   */
  const held = new Set(skills.map((row) => row.skillId));
  const offerable = vocabulary.filter((skill) => !held.has(skill.id));

  /** ⚠️ The middle level by default, as `skPickOpen` does — never the weakest. */
  const defaultLevel = levels[Math.floor(levels.length / 2)] ?? levels[0];

  const loading = props.loading === true;

  const note = loading
    ? ''
    : skills.length === 0
      ? t('account.skills.none')
      : [
          t('account.skills.count', { count: String(skills.length) }),
          t('account.skills.inCategories', {
            count: String(new Set(skills.map((row) => row.categoryName)).size),
          }),
        ].join(' · ');

  const addItems: MenuItem[] = offerable.map((skill) => ({
    label: skill.name,
    hint: skill.categoryName,
    onSelect: () => {
      if (defaultLevel === undefined) return;
      props.onAdd(skill.id, defaultLevel.id);
    },
  }));

  return (
    <div className="mx-auto flex max-w-[680px] flex-col gap-3">
      {props.serverError !== null && (
        <p
          role="alert"
          className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-[12.5px]"
        >
          {props.serverError}
        </p>
      )}

      <div className="bg-card text-card-foreground rounded-xl border shadow-sm">
        <div className="flex items-center gap-3 border-b px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium">{t('account.section.skills')}</div>
            <div className="text-muted-foreground mt-0.5 text-[12px]">{note}</div>
          </div>
          {/* ⚠️ Disabled rather than hidden when the vocabulary is exhausted — the hint below
              says who can add to it, which is the one useful thing at that moment.
              ⚠️ ABSENT entirely without `people.manage`: a control somebody may not use is not a
              disabled control, it is a control that should not be on their screen. */}
          {!canEdit ? null : (
            <DropdownMenu
              items={addItems}
              align="end"
              width={300}
              label={t('account.skills.add')}
              /* 17 Oct 2026 · a vocabulary is long; a search makes its tail reachable on a phone. */
              searchPlaceholder={t('list.searchOptions')}
            >
              <Button size="sm" className="gap-1.5" disabled={addItems.length === 0}>
                <Icon name="plus" size={14} />
                {t('account.skills.add')}
              </Button>
            </DropdownMenu>
          )}
        </div>

        <SkillRows
          skills={skills}
          levels={levels}
          canEdit={canEdit}
          loading={loading}
          emptyText={t('account.skills.empty')}
          onSetLevel={props.onSetLevel}
          onRemove={props.onRemove}
        />
      </div>

      <p className="text-muted-foreground flex items-start gap-2 px-1 text-[11.5px] leading-relaxed">
        <Icon name="circle-dot" size={13} className="mt-0.5 shrink-0" />
        <span>{t('account.skills.hint')}</span>
      </p>
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The rows of somebody's skills — the table both Skilljeim and a person use. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Drawn with no frame of its own: the account page puts it in its card, the person page in its
 * section's bordered box (13 Oct 2026 — a card inside that box read as a box in a box). Each row
 * edits its level in place; adding happens wherever the caller draws its picker.
 */
export function SkillRows({
  skills,
  levels,
  canEdit,
  loading,
  emptyText,
  onSetLevel,
  onRemove,
}: {
  readonly skills: readonly MemberSkill[];
  readonly levels: readonly SkillLevel[];
  readonly canEdit: boolean;
  readonly loading: boolean;
  /** Already translated — what an empty list says. */
  readonly emptyText: string;
  readonly onSetLevel: (skillId: string, levelId: string) => void;
  readonly onRemove: (skillId: string) => void;
}): React.JSX.Element {
  const t = useT();
  return (
    <>
      {skills.map((row) => (
        <div
          key={row.id}
          className="group hover:bg-accent/40 flex items-center gap-2.5 border-b px-3 py-2 transition-colors last:border-0"
        >
          <span
            aria-hidden
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: row.categoryColor }}
          />
          <span className="min-w-[110px] flex-1 truncate text-[12.5px] font-medium">
            {row.skillName}
          </span>
          <span className="text-muted-foreground w-[128px] shrink-0 truncate text-[11.5px]">
            {row.categoryName}
          </span>

          {/* ⚠️ Read-only shows the level as text: a listbox that refuses every choice is a
            control that lies about what it does. */}
          {!canEdit ? (
            <span className="flex h-7 w-[132px] shrink-0 items-center gap-2 px-2 text-[12px]">
              <span
                aria-hidden
                className="size-2 shrink-0 rounded-full"
                style={{ background: row.levelColor }}
              />
              <span className="min-w-0 flex-1 truncate">{row.levelName}</span>
            </span>
          ) : (
            <DropdownMenu
              items={levels.map((level) => ({
                label: level.name,
                /* ⚠️ The description as the SECOND LINE, never the `hint`: a hint is the short
                   right-hand tag (a key, a shortcut), and a sentence there squeezed the level's
                   name down to its first letter (13 Oct 2026). */
                ...(level.description === null || level.description === ''
                  ? {}
                  : { description: level.description }),
                dot: level.color,
                checked: level.id === row.levelId,
                onSelect: () => {
                  if (level.id === row.levelId) return;
                  onSetLevel(row.skillId, level.id);
                },
              }))}
              align="end"
              width={300}
              label={t('account.skills.levelOf', { name: row.skillName })}
            >
              <button
                type="button"
                className="border-input hover:bg-accent/60 flex h-7 w-[132px] shrink-0 items-center gap-2 rounded-md border px-2 text-left text-[12px] shadow-xs transition-colors"
              >
                <span
                  aria-hidden
                  className="size-2 shrink-0 rounded-full"
                  style={{ background: row.levelColor }}
                />
                <span className="min-w-0 flex-1 truncate">{row.levelName}</span>
                <Icon name="chevron-down" size={12} className="text-muted-foreground opacity-70" />
              </button>
            </DropdownMenu>
          )}

          {!canEdit ? null : (
            <Button
              variant="ghost"
              size="icon"
              aria-label={t('account.skills.remove', { name: row.skillName })}
              className="text-muted-foreground hover:text-destructive size-7 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              onClick={() => {
                onRemove(row.skillId);
              }}
            >
              <Icon name="trash" size={13} />
            </Button>
          )}
        </div>
      ))}

      {loading &&
        SKELETON_WIDTHS.slice(0, 6).map((_widths, index) => (
          <div
            key={`skeleton-${String(index)}`}
            aria-hidden
            className="flex h-[45px] items-center gap-2.5 border-b px-3 last:border-0"
          >
            <Skeleton className="size-2.5 rounded-full" />
            <div className="min-w-[110px] flex-1">
              <Skeleton width={skeletonWidth(index, 0)} />
            </div>
            <div className="w-[128px] shrink-0">
              <Skeleton width={skeletonWidth(index, 1)} />
            </div>
            <Skeleton width="132px" className="h-7 shrink-0" />
          </div>
        ))}
      {loading && (
        <div role="status" aria-live="polite" className="sr-only">
          {t('state.loading')}
        </div>
      )}

      {!loading && skills.length === 0 && (
        <p className="text-muted-foreground px-3 py-8 text-center text-[12px]">{emptyText}</p>
      )}
    </>
  );
}
